import { Check } from 'lucide-react';
import React from 'react';

interface Step {
  name: string;
  step: number;
  subtitle?: string;
  icon: React.ReactNode;
}

interface ColorScheme {
  activeStep?: string;
  completedStep?: string;
  inactiveStep?: string;
  activeText?: string;
  completedText?: string;
  inactiveText?: string;
  completedIcon?: string;
  progressBar?: string;
}

interface StepIndicatorProps {
  steps: Step[];
  currentStep: number;
  onStepClick?: (step: number) => void;
  colors?: ColorScheme;
}

const MobileStepIndicator: React.FC<StepIndicatorProps> = ({
  steps,
  currentStep,
  onStepClick,
  colors = {}
}) => {
  // Default colors
  const defaultColors: ColorScheme = {
  activeStep: "#DDB889",
  completedStep: "#22c55e",  // ✨ Green for completed!
  inactiveStep: "#DDB889/20",
  activeText: "#DDB889",
  completedText: "#22c55e",  // ✨ Green text for completed!
  inactiveText: "#DDB889/40",
  completedIcon: "#FFFFFF",  // White checkmark on green
  progressBar: "#22c55e"  // ✨ Green progress bar!
};

  // Merge provided colors with defaults
  const theme = {
    activeStep: colors.activeStep || defaultColors.activeStep,
    completedStep: colors.completedStep || defaultColors.completedStep,
    inactiveStep: colors.inactiveStep || defaultColors.inactiveStep,
    activeText: colors.activeText || defaultColors.activeText,
    completedText: colors.completedText || defaultColors.completedText,
    inactiveText: colors.inactiveText || defaultColors.inactiveText,
    completedIcon: colors.completedIcon || defaultColors.completedIcon,
    progressBar: colors.progressBar || defaultColors.progressBar
  };

  const currentStepIndex = steps.findIndex(s => s.step === currentStep);
  const visibleSteps = steps.slice(currentStepIndex, currentStepIndex + 3);
  const isLastStepOnly = visibleSteps.length === 1;

  return (
    <div className="lp-relative lp-bg-black lp-overflow-hidden">
      {/* Main content */}
      <div className="lp-relative lp-z-10 lp-p-4 lp-bg-black/95 lp-backdrop-blur-sm lp-border lp-border-[#DDB889]/10 lp-rounded-2xl
        lp-shadow-[0_0_15px_rgba(221,184,137,0.07)] hover:lp-shadow-[0_0_20px_rgba(221,184,137,0.1)]
        lp-transition-all lp-duration-700">
        <div className={`lp-flex lp-items-center ${isLastStepOnly ? 'lp-justify-center' : 'lp-justify-between'}`}>
          {visibleSteps.map((step, index) => {
            const isCompleted = currentStep > step.step;
            const isCurrent = currentStep === step.step;
            const isLast = index === visibleSteps.length - 1;

            return (
              <React.Fragment key={step.name}>
                {/* Step container */}
                <div className="lp-flex lp-flex-col lp-items-center lp-group">
                  <div
  className={`
    lp-w-8 lp-h-8 lp-rounded-full lp-flex lp-items-center lp-justify-center
    lp-transition-all lp-duration-500 lp-ease-in-out
    ${onStepClick ? 'lp-cursor-pointer hover:lp-scale-105' : ''}
  `}
  style={{
    backgroundColor: isCompleted
      ? theme.completedStep
      : 'black',
    borderWidth: isCompleted
      ? '0px'
      : isCurrent ? '2px' : '1px',
    borderColor: isCurrent
      ? theme.activeStep
      : theme.inactiveStep,
    boxShadow: isCompleted
      ? '0 0 15px rgba(34,197,94,0.3)'  // ✨ Green glow for completed!
      : isCurrent ? '0 0 15px rgba(221,184,137,0.1)' : 'none'
  }}
  onClick={() => onStepClick && onStepClick(step.step)}
>
                    {isCompleted ? (
                      <Check size={16} style={{ color: theme.completedIcon }} />
                    ) : (
                      React.cloneElement(step.icon as React.ReactElement, {
                        size: 16,
                        className: `lp-transition-transform lp-duration-300 ${
                          isCurrent ? 'group-hover:lp-scale-110' : ''
                        }`,
                        style: {
                          color: isCurrent
                            ? theme.activeText
                            : theme.inactiveText
                        }
                      })
                    )}
                  </div>

                  <div
                    className="lp-mt-2 lp-text-center lp-cursor-pointer lp-w-16"
                    onClick={() => onStepClick && onStepClick(step.step)}
                  >
                    <div className="lp-text-xs lp-font-medium lp-transition-all lp-duration-300"
                      style={{
                        color: isCompleted
                          ? theme.completedText
                          : isCurrent ? theme.activeText : theme.inactiveText
                      }}>
                      {step.name}
                      {step.subtitle && (
                        <span className="lp-block lp-text-[10px] lp-mt-0.5 lp-opacity-70">
                          {step.subtitle}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Connecting line with enhanced styling */}
                {!isLast && !isLastStepOnly && (
                  <div className="lp-w-12 lp-h-0.5 lp-bg-[#DDB889]/10 lp-relative lp-rounded-full">
                    <div
                      className="lp-absolute lp-inset-0 lp-rounded-full lp-transition-all lp-duration-700 lp-ease-in-out"
                      style={{
                        width: isCompleted ? '100%' : '0%',
                        backgroundColor: theme.progressBar,
                        boxShadow: isCompleted ? '0 0 10px rgba(221,184,137,0.2)' : 'none'
                      }}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Progress indicator with enhanced styling */}
        {currentStepIndex < steps.length - 3 && (
          <div className="lp-text-xs lp-text-center lp-mt-3"
            style={{ color: `${theme.inactiveText?.split('/')[0]}/60` }}>
            {steps.length - (currentStepIndex + 3)} steps remaining
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileStepIndicator;
